/**
 * RoleLens — domain selection primary / watch / out (FE-ROLE-1).
 * Profile is data-driven when present; provisional FE-ROLE-2 hypotheses otherwise.
 */

import { inScope, type ScopeContext } from '../filters';
import { scopeIdsEqual } from '../../../utils/scopeIdPlane';

export type RoleLensTier = 'primary' | 'watch' | 'out';

export interface RoleLensDomain {
  domainRef: string;
  /** Optional display label from structure */
  label?: string;
  tier: RoleLensTier;
}

export interface RoleLens {
  /** Stable role id (e.g. gm, quotes, purchasing, owner, client) */
  roleId: string;
  domains: RoleLensDomain[];
  source: 'manifest' | 'provisional';
}

export type RoleLensDensity = 'item' | 'strip' | 'hidden';

export function densityForTier(tier: RoleLensTier): RoleLensDensity {
  if (tier === 'primary') return 'item';
  if (tier === 'watch') return 'strip';
  return 'hidden';
}

export function tierForDomain(lens: RoleLens | null | undefined, domainRef: string): RoleLensTier {
  if (!lens || !domainRef) return 'primary';
  const hit = lens.domains.find((d) => scopeIdsEqual(d.domainRef, domainRef));
  return hit?.tier ?? 'out';
}

/** True when domain should render at all (primary or watch). */
export function isDomainVisible(lens: RoleLens | null | undefined, domainRef: string): boolean {
  return tierForDomain(lens, domainRef) !== 'out';
}

/** Filter scopable items by RoleLens (out domains excluded). Null lens = pass-through. */
export function passesRoleLens(
  item: { domain?: string; filterScope?: string },
  lens: RoleLens | null | undefined,
  scopeContext?: ScopeContext
): boolean {
  if (!lens) return true;
  const domain = item.domain ?? item.filterScope;
  if (!domain) return true;
  // If any primary/watch domain covers this item via structure children, keep it.
  for (const d of lens.domains) {
    if (d.tier === 'out') continue;
    if (inScope(domain, d.domainRef, scopeContext)) return true;
  }
  return false;
}

/** Surfaces the current role does not own stay in nav but dimmed (FE-NAV-6).
 * Client owns Drive; Client Status when that tab is offered (FE-SURF-2).
 * Process Flow is retired (FE-SURF-1 2026-09-30). Internal provisional roles
 * (FE-ROLE-2) own the internal set and not Client Status.
 */
export function surfaceOwnedByLens(
  surfaceKey: string,
  lens: RoleLens | null | undefined
): boolean {
  if (lens?.roleId === 'client') {
    return surfaceKey === 'client' || surfaceKey === 'drive';
  }
  if (surfaceKey === 'client') return false;
  return true;
}

/**
 * Provisional FE-ROLE-2 hypotheses keyed by role id.
 * Operating lens until daily-sketch intake confirms selections (req §12).
 * Domain refs are pattern tokens matched against structure scope ids (substring / suffix).
 */
export const PROVISIONAL_ROLE_LENS_PATTERNS: Record<
  string,
  Array<{ match: RegExp; tier: RoleLensTier }>
> = {
  gm: [
    { match: /delivery|supply/i, tier: 'primary' },
    { match: /demand|resource/i, tier: 'watch' },
  ],
  quotes: [
    { match: /demand|intake|quote/i, tier: 'primary' },
    { match: /supply/i, tier: 'watch' },
  ],
  purchasing: [
    { match: /supply/i, tier: 'primary' },
    { match: /delivery/i, tier: 'watch' },
  ],
  owner: [
    { match: /finance|strategy|demand/i, tier: 'primary' },
    { match: /delivery|supply|resource/i, tier: 'watch' },
  ],
  client: [{ match: /delivery/i, tier: 'primary' }],
};

export interface StructureDomainNode {
  id: string;
  label?: string;
}

/** Subdomain children of every domain (FE-ROLE-2). Falls back to top-level domain nodes. */
export function structureNodesForProvisionalLens(structure: {
  groups: Array<{
    id: string;
    nodes: Array<{
      filterScope?: string;
      key: string;
      label?: string;
      children?: Array<{ filterScope?: string; key: string; label?: string }>;
    }>;
  }>;
}): StructureDomainNode[] {
  const domainGroup = structure.groups.find((g) => g.id === 'domains');
  const children: StructureDomainNode[] = [];
  for (const domain of domainGroup?.nodes ?? []) {
    for (const child of domain.children ?? []) {
      children.push({ id: child.filterScope || child.key, label: child.label });
    }
  }
  if (children.length) return children;
  return (domainGroup?.nodes ?? []).map((n) => ({
    id: n.filterScope || n.key,
    label: n.label,
  }));
}

/**
 * Build a RoleLens by matching structure domain ids against provisional patterns.
 * Domains with no match are `out`.
 */
export function deriveProvisionalRoleLens(
  roleId: string,
  domains: StructureDomainNode[]
): RoleLens {
  const patterns = PROVISIONAL_ROLE_LENS_PATTERNS[roleId] ?? [];
  const mapped: RoleLensDomain[] = domains.map((d) => {
    const hay = `${d.id} ${d.label ?? ''}`;
    let tier: RoleLensTier = 'out';
    for (const p of patterns) {
      if (p.match.test(hay)) {
        tier = p.tier;
        break;
      }
    }
    return { domainRef: d.id, label: d.label, tier };
  });
  return { roleId, domains: mapped, source: 'provisional' };
}

/** Parse RoleLens from render-manifest section `role-lens` when present. */
export function parseRoleLensFromManifest(manifest: {
  sections: Array<{ id: string; meta?: object; queue?: unknown[] }>;
}): RoleLens | null {
  const section = manifest.sections.find((s) => s.id === 'role-lens');
  if (!section) return null;
  const meta = (section.meta ?? {}) as Record<string, unknown>;
  const roleId = typeof meta.roleId === 'string' ? meta.roleId : 'unknown';
  const domainsRaw = Array.isArray(meta.domains) ? meta.domains : section.queue ?? [];
  const domains: RoleLensDomain[] = [];
  for (const row of domainsRaw) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const domainRef =
      (typeof r.domainRef === 'string' && r.domainRef) ||
      (typeof r.domain_ref === 'string' && r.domain_ref) ||
      (typeof r.itemId === 'string' && r.itemId) ||
      (typeof r.filterScope === 'string' && r.filterScope) ||
      '';
    if (!domainRef) continue;
    const tierRaw = String(r.tier ?? r.roleTier ?? 'out').toLowerCase();
    const tier: RoleLensTier =
      tierRaw === 'primary' || tierRaw === 'watch' || tierRaw === 'out' ? tierRaw : 'out';
    const label =
      (typeof r.label === 'string' && r.label) ||
      (typeof r.title === 'string' && r.title) ||
      undefined;
    domains.push({ domainRef, label, tier });
  }
  if (!domains.length) return null;
  return { roleId, domains, source: 'manifest' };
}
